import { api } from "./api";

export interface AnonymizeRequest {
  title: string;
  company: string;
}

export interface InterviewDescriptionRequest {
  date?: Date | string;
  firstName: string;
  lastName: string;
  anonymizedAttribution: string;
}

/**
 * Generate anonymized attribution using ChatGPT
 * Converts "Northeast America Director at SAP" to "Director, SAP"
 */
export const generateAnonymizedAttribution = async (
  title: string,
  company: string
): Promise<string | null> => {
  try {
    const response = await api.post("/api/ai/anonymize-attribution", {
      title,
      company,
    });

    if (response.data.success) {
      return response.data.anonymizedAttribution;
    }
    return null;
  } catch (error) {
    console.error("Error generating anonymized attribution:", error);
    return null;
  }
};

/**
 * Generate interview description in format: mm/dd_firstname_lastname_anonymousTitle_company
 */
export const generateInterviewDescription = (
  date: Date | string | undefined,
  firstName: string,
  lastName: string,
  anonymizedAttribution: string
): string => {
  // Format date as mm/dd
  let formattedDate = "";
  if (date) {
    const dateObj = typeof date === "string" ? new Date(date) : date;
    if (!isNaN(dateObj.getTime())) {
      const month = (dateObj.getMonth() + 1).toString().padStart(2, "0");
      const day = dateObj.getDate().toString().padStart(2, "0");
      formattedDate = `${month}/${day}`;
    }
  }

  // Clean up names for filename
  const cleanFirstName = firstName.replace(/[^a-zA-Z0-9]/g, "");
  const cleanLastName = lastName.replace(/[^a-zA-Z0-9]/g, "");

  // Parse anonymized attribution to separate title and company
  // Expected format: "Director, SAP" -> "Director_SAP"
  let cleanAttribution = "";
  if (anonymizedAttribution.includes(",")) {
    const parts = anonymizedAttribution.split(",").map((part) => part.trim());
    const cleanTitle = parts[0].replace(/[^a-zA-Z0-9]/g, "");
    const cleanCompany = parts[1] ? parts[1].replace(/[^a-zA-Z0-9]/g, "") : "";
    cleanAttribution = cleanCompany
      ? `${cleanTitle}_${cleanCompany}`
      : cleanTitle;
  } else {
    // Fallback if no comma found
    cleanAttribution = anonymizedAttribution.replace(/[^a-zA-Z0-9]/g, "");
  }

  // Construct the description
  const parts = [
    formattedDate,
    cleanFirstName,
    cleanLastName,
    cleanAttribution,
  ].filter(Boolean); // Remove empty parts

  return parts.join("_");
};

/**
 * Batch generate anonymized attributions for multiple interviews
 */
export const batchGenerateAnonymizedAttributions = async (
  interviews: InterviewDescriptionRequest[]
): Promise<Record<string, string>> => {
  try {
    const response = await api.post("/api/ai/batch-anonymize-attribution", {
      interviews,
    });

    if (response.data.success) {
      return response.data.attributions;
    } else {
      console.error(
        "Error in batch attribution generation:",
        response.data.error
      );
      return {};
    }
  } catch (error) {
    console.error("Error calling batch attribution API:", error);
    return {};
  }
};

export const parseCalendarText = async (
  calendarText: string,
  projectId?: string
): Promise<any> => {
  try {
    const response = await api.post("/api/ai/parse-calendar-text", {
      calendarText,
      projectId, // Pass project ID for context-aware parsing
    });

    if (response.data.success) {
      return response.data.interviewData;
    } else {
      console.error("Error parsing calendar text:", response.data.error);
      throw new Error(response.data.error || "Failed to parse calendar text");
    }
  } catch (error) {
    console.error("Error calling calendar text parsing API:", error);
    throw error;
  }
};

export const calculatePrice = async (
  network: string,
  duration: string | number,
  price?: string | number,
  currentTotal?: string | number,
  currentDuration?: string | number
): Promise<number | null> => {
  try {
    const response = await api.post("/api/ai/calculate-price", {
      network,
      duration: String(duration),
      ...(price !== undefined ? { price: String(price) } : {}),
      ...(currentTotal !== undefined
        ? { currentTotal: String(currentTotal) }
        : {}),
      ...(currentDuration !== undefined
        ? { currentDuration: String(currentDuration) }
        : {}),
    });

    if (response.data.success) {
      return Number(response.data.calculatedPrice || 0);
    }
    return null;
  } catch (error) {
    console.error("Error calculating price:", error);
    return null;
  }
};
